import { Add as AddIcon } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Palette } from 'Palette';
import { ContentItem } from '../../../models/ContentManagementInterface';
import { PALETTE_BLOCKS } from './blockPaletteBlocks';
import { PALETTE_DRAG_MIME } from './blockPaletteConstants';
import { useBlockPaletteDrag } from './blockPaletteDragContext';
import { CanvasSmartPasteConfig, useCanvasSmartPaste } from './useCanvasSmartPaste';

interface BlockDropZoneProps {
  /** Anchor block index; -1 for the always-visible top zone. */
  index: number;
  where: 'before' | 'after';
  onDrop: (index: number, type: ContentItem['type'], where: 'before' | 'after') => void;
  /** Render as the large, prominent target rather than the thin inter-block gap. */
  emptyState?: boolean;
  /**
   * `emptyState` only: also resolves a clipboard paste or a dropped file (Excel/Word/PDF/image)
   * into whichever block(s) best fit, alongside dragging a palette block onto this zone.
   */
  smartPaste?: CanvasSmartPasteConfig;
}

const hasBlockPayload = (event: React.DragEvent): boolean =>
  (event.dataTransfer.types ?? []).includes(PALETTE_DRAG_MIME);

const hasFilePayload = (event: React.DragEvent): boolean =>
  (event.dataTransfer.types ?? []).includes('Files') || (event.dataTransfer.files?.length ?? 0) > 0;

function dropZoneMessage(opts: {
  blockDragActive: boolean;
  blockDragOver: boolean;
  fileDragOver: boolean;
  label: string;
  smartPasteEnabled: boolean;
  armed: boolean;
  busy: boolean;
}): string {
  const { blockDragActive, blockDragOver, fileDragOver, label, smartPasteEnabled, armed, busy } =
    opts;
  if (busy) return 'Reading the clipboard — click Paste if your browser asks';
  if (armed) return 'Press Ctrl+V now';
  if (fileDragOver) return 'Drop to import this file';
  if (blockDragActive) return blockDragOver ? `Drop to add ${label}` : `Drop ${label} here`;
  return smartPasteEnabled
    ? 'Drag a block here to add content, or click to paste and let AI structure it'
    : 'Drag a block here to add your first section';
}

function dropZoneMinHeight(emptyState: boolean, emphasised: boolean): number {
  if (emptyState) return 140;
  return emphasised ? 56 : 40;
}

function dropZoneSx(
  emptyState: boolean,
  interactive: boolean,
  emphasised: boolean,
  clickable: boolean,
) {
  return {
    my: emptyState ? 0 : 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    textAlign: 'center',
    borderRadius: 2,
    border: '2px dashed',
    borderColor: emphasised ? Palette.default.buttonBg : Palette.default.border,
    bgcolor: emphasised ? Palette.default.activeBg : Palette.tone1.background,
    color: emphasised ? Palette.default.buttonBg : Palette.default.disabledText,
    minHeight: dropZoneMinHeight(emptyState, emphasised),
    transition: 'min-height 0.12s ease, background-color 0.12s ease, border-color 0.12s ease',
    pointerEvents: interactive ? 'auto' : 'none',
    cursor: clickable ? 'pointer' : 'default',
    '& *': { pointerEvents: 'none' },
  } as const;
}

const NO_SMART_PASTE: CanvasSmartPasteConfig = {
  modelId: '',
  modelType: 'FundOpportunity',
  onResolved: () => {},
};

/**
 * Drag-and-drop for one zone: a palette block drops in at `index`; with smart paste on, a dropped
 * file goes to `dropFile` instead.
 */
function useDropZoneDrag(opts: {
  index: number;
  where: 'before' | 'after';
  onDrop: BlockDropZoneProps['onDrop'];
  dropFile: ((file: File) => Promise<void> | void) | null;
}) {
  const { index, where, onDrop, dropFile } = opts;
  const [isOver, setIsOver] = useState(false);
  const [fileOver, setFileOver] = useState(false);

  const handleDragOver = (event: React.DragEvent) => {
    if (hasBlockPayload(event)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setIsOver(true);
      return;
    }
    if (dropFile && hasFilePayload(event)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setFileOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsOver(false);
    setFileOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    handleDragLeave();
    if (hasBlockPayload(event)) {
      const type = event.dataTransfer.getData(PALETTE_DRAG_MIME) as ContentItem['type'];
      if (type) onDrop(index, type, where);
      return;
    }
    const file = event.dataTransfer.files?.[0];
    if (dropFile && file) void dropFile(file);
  };

  return {
    isOver,
    fileOver,
    handlers: {
      onDragEnter: handleDragOver,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}

/** Makes the zone a keyboard-accessible "paste" button when smart paste is on. */
function smartPasteButtonProps(enabled: boolean, run: () => void) {
  if (!enabled) return {};
  return {
    role: 'button',
    tabIndex: 0,
    'aria-label': 'Paste content to create a block',
    onClick: run,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        run();
      }
    },
  };
}

const paletteBlockLabel = (type: ContentItem['type'] | null): string =>
  PALETTE_BLOCKS.find((block) => block.type === type)?.label ?? 'block';

function DropZoneBody({
  busy,
  error,
  message,
}: {
  busy: boolean;
  error: string | null;
  message: string;
}) {
  return (
    <>
      {busy ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {message}
      </Typography>
      {error && (
        <Typography variant="caption" color="error.main">
          {error}
        </Typography>
      )}
    </>
  );
}

/**
 * Insertion target between blocks (and the large, always-visible target above the first one).
 * Dragging a palette block over any zone reveals it; the one under the pointer emphasises and
 * accepts the drop. The large top zone additionally accepts a clipboard paste (click, or Ctrl+V
 * once armed) or a dropped file when `smartPaste` is given — see `useCanvasSmartPaste`.
 */
export default function BlockDropZone({
  index,
  where,
  onDrop,
  emptyState = false,
  smartPaste,
}: BlockDropZoneProps) {
  const { draggingType } = useBlockPaletteDrag();
  const blockDragActive = draggingType !== null;
  const smartPasteEnabled = Boolean(smartPaste);
  const paste = useCanvasSmartPaste(smartPaste ?? NO_SMART_PASTE);
  const drag = useDropZoneDrag({
    index,
    where,
    onDrop,
    dropFile: smartPasteEnabled ? paste.dropFile : null,
  });

  if (!blockDragActive && !emptyState) {
    // Idle: just a small gap between blocks, nothing interactive.
    return <Box data-testid="content-block-drop-zone" aria-hidden sx={{ height: 10 }} />;
  }

  return (
    <Box
      data-testid="content-block-drop-zone"
      {...smartPasteButtonProps(smartPasteEnabled, paste.run)}
      {...drag.handlers}
      sx={dropZoneSx(
        emptyState,
        blockDragActive || smartPasteEnabled,
        drag.isOver || drag.fileOver,
        smartPasteEnabled && !blockDragActive,
      )}
    >
      <DropZoneBody
        busy={paste.busy}
        error={paste.error}
        message={dropZoneMessage({
          blockDragActive,
          blockDragOver: drag.isOver,
          fileDragOver: drag.fileOver,
          label: paletteBlockLabel(draggingType),
          smartPasteEnabled,
          armed: paste.armed,
          busy: paste.busy,
        })}
      />
      {smartPasteEnabled && <Box {...paste.hiddenInputProps} />}
    </Box>
  );
}
